import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../../api/supabase'

// Z50.X: Collect From / Deliver To / Haulier need three input modes -- pick from the address
// book (warehouses/companies/couriers), free text (temporary address not in the address book),
// or clear. FK columns (customerId/warehouseId) are set when picked from the address book so a
// future re-sync is possible; both null means free text.
//
// "Companies" here is the `customers` table -- there is no separate companies/address-book
// table in the schema. `customers` already holds every party CY UK addresses mail to
// (customers, consignees, third-party warehouses, suppliers, etc.), not just paying customers;
// customer_type/company_types/industry_type exist as columns but are unpopulated across all
// 231 rows as of 2026-09-23, so there's no sub-typing to group by yet.
//
// Flat list, no grouping/type-filtering/priority-sorting by design (Gary, 2026-09-23) -- all
// three fields share this exact component and search the exact same set of rows; the only
// difference between them is what gets committed on selection, via `nameOnly`: Collect
// From/Deliver To keep the full address + phone, Haulier keeps only the company name (matches
// the printed note, which never shows a Haulier address).
export interface PickedAddress {
  name: string
  lines: string[]
  phone: string | null
  customerId: string | null
  warehouseId: string | null
}

export function blankAddress(): PickedAddress {
  return { name: '', lines: [], phone: null, customerId: null, warehouseId: null }
}

export function addressDisplay(v: PickedAddress): string[] {
  return [v.name, ...v.lines].filter(Boolean)
}

function warehouseAddrLines(addr: unknown): string[] {
  if (!addr || typeof addr !== 'object') return []
  const a = addr as Record<string, string>
  return [a.line1, a.line2, a.city, a.postcode].filter(Boolean)
}

// Common couriers aren't customers/suppliers in the address book (no FK) -- kept as quick
// free-text presets, same convention as the old COURIER_NAMES datalist it replaces.
const COURIER_PRESETS = ['DPD', 'UPS', 'EVRi', 'Amazon', 'FedEx']

interface SearchRow {
  key: string
  label: string
  value: PickedAddress
}

function nameOnlyOf(v: PickedAddress): PickedAddress {
  return { ...v, lines: [], phone: null }
}

export function AddressField({ value, autoValue, onCommit, className, nameOnly }: {
  value: PickedAddress
  autoValue?: PickedAddress | null // the "smart default" this field can reset to, if any
  onCommit: (v: PickedAddress) => void
  className?: string
  // Haulier only shows/keeps the company name, never an address or phone (matches the printed
  // note) -- Collect From / Deliver To keep the full picked address.
  nameOnly?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const lines = nameOnly ? [value.name].filter(Boolean) : addressDisplay(value)
  const isAuto = !!autoValue && !!value.warehouseId && value.warehouseId === autoValue.warehouseId
  const isEmpty = lines.length === 0

  if (editing) {
    return <AddressPickerPanel value={value} nameOnly={nameOnly} onCommit={v => { onCommit(v); setEditing(false) }} onCancel={() => setEditing(false)} />
  }
  return (
    <span className={`group inline-block ${className || ''}`}>
      <span onClick={() => setEditing(true)} className="cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded px-0.5 -mx-0.5 inline-block min-w-[1em] min-h-[1em]">
        {isEmpty
          ? <span className="text-gray-300 italic">—</span>
          : lines.map((l, i) => <div key={i}>{l}</div>)}
      </span>
      {autoValue && (
        <span
          title={isAuto ? 'Auto-filled from warehouse -- click a different address to override' : 'Overridden -- click to reset to warehouse default'}
          onClick={() => !isAuto && onCommit(autoValue)}
          className={`ml-1 text-[10px] leading-none align-top ${isAuto ? 'text-gray-300' : 'text-amber-500 cursor-pointer hover:text-amber-600'}`}
        >
          {isAuto ? '🔄' : '✏️'}
        </span>
      )}
    </span>
  )
}

function AddressPickerPanel({ value, onCommit, onCancel, nameOnly }: {
  value: PickedAddress
  onCommit: (v: PickedAddress) => void
  onCancel: () => void
  nameOnly?: boolean
}) {
  const [mode, setMode] = useState<'search' | 'freetext'>(value.customerId || value.warehouseId || !value.name ? 'search' : 'freetext')
  const [query, setQuery] = useState('')
  const [staticRows, setStaticRows] = useState<SearchRow[]>([])
  const [customerRows, setCustomerRows] = useState<SearchRow[]>([])
  const [freeName, setFreeName] = useState(value.name)
  const [freeLines, setFreeLines] = useState(value.lines.join('\n'))
  const [freePhone, setFreePhone] = useState(value.phone || '')
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (mode === 'search') inputRef.current?.focus()
  }, [mode])

  // Warehouses/couriers: small, fetched once. Companies (customers table): queried live per keystroke.
  useEffect(() => {
    let cancelled = false
    async function loadStatic() {
      const { data: whs } = await supabase.from('warehouses').select('id, code, name, address').eq('is_active', true)
      if (cancelled) return
      const rows: SearchRow[] = []
      for (const w of (whs as any[] | null) || []) {
        const addrLines = warehouseAddrLines(w.address)
        rows.push({ key: `w-${w.id}`, label: [w.name, addrLines[0]].filter(Boolean).join(' — '), value: {
          name: w.name, lines: addrLines, phone: null, customerId: null, warehouseId: w.id,
        } })
      }
      for (const c of COURIER_PRESETS) {
        rows.push({ key: `c-${c}`, label: c, value: { name: c, lines: [], phone: null, customerId: null, warehouseId: null } })
      }
      setStaticRows(rows)
    }
    loadStatic()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) { setCustomerRows([]); return }
    let cancelled = false
    const t = setTimeout(async () => {
      const { data: custs } = await supabase.from('customers')
        .select('id, code, name, address_line_1, address_line_2, address_line_3, city, postcode, country, contact_phone')
        .eq('is_active', true)
        .or(`name.ilike.%${q}%,code.ilike.%${q}%,postcode.ilike.%${q}%,city.ilike.%${q}%,address_line_1.ilike.%${q}%`)
        .order('name').limit(15)
      if (cancelled) return
      setCustomerRows(((custs as any[] | null) || []).map(c => {
        const addrLines = [c.address_line_1, c.address_line_2, c.address_line_3, c.city, c.postcode, c.country].filter(Boolean)
        return {
          key: `cu-${c.id}`, label: [c.name, addrLines[0]].filter(Boolean).join(' — '), value: {
            name: c.name, lines: addrLines, phone: c.contact_phone || null, customerId: c.id, warehouseId: null,
          },
        }
      }))
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query])

  const q = query.trim().toLowerCase()
  const filteredStatic = q ? staticRows.filter(r => r.label.toLowerCase().includes(q)) : staticRows
  // Flat, unsorted, ungrouped by design -- see the file-header comment.
  const allRows = [...filteredStatic, ...customerRows]

  return (
    <div ref={panelRef} className="relative inline-block" onClick={e => e.stopPropagation()}>
      <div className="absolute z-20 top-0 left-0 w-72 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl text-black dark:text-white text-[11px]">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button onClick={() => setMode('search')} className={`flex-1 px-2 py-1.5 font-medium ${mode === 'search' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-500'}`}>Address Book</button>
          <button onClick={() => setMode('freetext')} className={`flex-1 px-2 py-1.5 font-medium ${mode === 'freetext' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-500'}`}>Free Text</button>
        </div>

        {mode === 'search' ? (
          <div className="p-2">
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search company or warehouse…"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-[11px] dark:bg-gray-700" />
            <div className="max-h-56 overflow-y-auto mt-1.5">
              {allRows.length === 0 && <div className="text-gray-400 px-1 py-2">{q ? 'No matches' : 'Type to search, or pick a warehouse/courier below'}</div>}
              {allRows.map(r => (
                <button key={r.key} onClick={() => onCommit(nameOnly ? nameOnlyOf(r.value) : r.value)}
                  className="block w-full text-left px-1.5 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30">
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-2 space-y-1.5">
            <input value={freeName} onChange={e => setFreeName(e.target.value)} placeholder="Company / name"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-[11px] dark:bg-gray-700" />
            {!nameOnly && (
              <>
                <textarea value={freeLines} onChange={e => setFreeLines(e.target.value)} rows={3} placeholder="Address, one line each"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-[11px] dark:bg-gray-700 resize-none" />
                <input value={freePhone} onChange={e => setFreePhone(e.target.value)} placeholder="Phone (optional)"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-[11px] dark:bg-gray-700" />
              </>
            )}
            <button
              onClick={() => onCommit({
                name: freeName.trim(),
                lines: nameOnly ? [] : freeLines.split('\n').map(s => s.trim()).filter(Boolean),
                phone: nameOnly ? null : (freePhone.trim() || null),
                customerId: null, warehouseId: null,
              })}
              className="w-full bg-blue-600 text-white rounded px-2 py-1 font-medium hover:bg-blue-700">
              Use this {nameOnly ? 'name' : 'address'}
            </button>
          </div>
        )}

        <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 px-2 py-1.5">
          <button onClick={() => onCommit(blankAddress())} className="text-gray-400 hover:text-red-500">Clear</button>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      </div>
    </div>
  )
}
